import { TaskStatuses, WorkflowStatuses } from '@opallabs/concepts'
import { getNestedProperty } from '@opallabs/lambda'
import { Content } from '@opallabs/wormhole/types'
import { isApproval } from '../../../helpers/matchers'
import { Phase } from '../types'

const approvalStrings = Object.freeze({
  apporvalUnsent: "Content hasn't been sent for approval yet",
  approved: 'Content has been approved',
  awaitingApproval: 'Content is awaiting approval',
  declined: 'Content has been declined',
  notStarted: 'Workflow has not yet started',
  taskPhase: 'Content is in a task phase',
})

function findFirstNonCompletePhase(phases: Array<Phase> = []): Phase | null {
  for (let i = 0; i < phases.length; i += 1) {
    const phase = phases[i]
    if (phase && phase.status && phase.status !== 'complete') {
      return phase
    }
  }
  return null
}

function findFirstApprovalPhase(phases: Array<Phase> = []): Phase | null {
  for (let i = 0; i < phases.length; i += 1) {
    const phase = phases[i]
    if (phase && phase.phase_type && isApproval(phase.phase_type)) {
      return phase
    }
  }
  return null
}

const hasOnlyCompletedTaskPhases = (phases: Array<Phase> = []): boolean => {
  const allPhasesComplete = !findFirstNonCompletePhase(phases)
  const hasOnlyTaskPhases = !findFirstApprovalPhase(phases)
  return allPhasesComplete && hasOnlyTaskPhases
}

const contentIsDeclined = (content: Partial<Content> | null = {}) =>
  getNestedProperty(content ?? {}, 'status') === 'needs_revision'

const contentIsPublished = (content: Partial<Content> | null = {}) =>
  getNestedProperty(content ?? {}, 'post_state') === 'published'

type GetApprovalParams = {
  content?: Partial<Content> | null
  finalApprovalReached?: boolean
  phases?: Array<Phase>
  workflowStarted?: boolean
}
type ApprovalContent = {
  status: WorkflowStatuses | TaskStatuses | null
  description: string
}

function getApprovalContent({
  content,
  finalApprovalReached = false,
  phases = [],
  workflowStarted = false,
}: GetApprovalParams): ApprovalContent {
  if (!workflowStarted) {
    return { status: null, description: approvalStrings.notStarted }
  }

  if (contentIsDeclined(content)) {
    return { status: 'approval-denied', description: approvalStrings.declined }
  }

  // an 'active phase' in this context is either any current Task Phase
  // or a current Approval Phase that is actively awaiting responses
  const activePhase = phases.find(
    (p) => p.status === 'active' || p.status === 'current'
  )
  if (finalApprovalReached && !activePhase) {
    return {
      status: 'approval-approved',
      description: approvalStrings.approved,
    }
  }

  if (activePhase) {
    if (isApproval(activePhase.phase_type)) {
      return {
        status: 'approval-pending',
        description: approvalStrings.awaitingApproval,
      }
    }
    return { status: 'tasks-pending', description: approvalStrings.taskPhase }
  }

  // a 'current phase' in this context is an Approval Phase that
  // is current, but has not yet been sent for Approval
  const currentPhase = findFirstNonCompletePhase(phases)
  const hasNonSentApprovalPhase =
    currentPhase && isApproval(currentPhase.phase_type)
  if (hasNonSentApprovalPhase || hasOnlyCompletedTaskPhases(phases)) {
    return {
      status: 'approval-not-sent',
      description: approvalStrings.apporvalUnsent,
    }
  }

  return { status: null, description: approvalStrings.notStarted }
}

export { contentIsDeclined, contentIsPublished, getApprovalContent }
